/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useState, useEffect, useCallback, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useCustomerId} from '@salesforce/commerce-sdk-react'
import {getProductAddToBasketItems, getProductsToAddToCart} from '../utils/product-utils'
import useEffectEvent from './use-effect-event'
import {useAddToCartImplementation} from './use-add-to-cart'
import {
    BonusProductsModalContext,
    BonusProductsModal,
    useBonusProductsModal
} from './use-bonus-products-modal'
import AddToCartModal from '../components/add-to-cart-modal'

/**
 * @typedef {{
 *   onClose: function,
 *   isOpen: boolean,
 *   finalFocusRef: object,
 *   data?: {product: object, itemsAdded: object[]},
 *   onOpen: function({onRecommendedProductClick?: function, onClose?: function}=),
 *   addProduct: function(product: object),
 *   onRecommendedProductClick?: function
 * }} AddToCartModalContextType
 */

/**
 * @typedef {{
 *   addProduct: function({
 *     product: object,
 *     includeInAddToCartModal?: boolean,
 *     showAddToCartModal?: boolean,
 *     onRecommendedProductClick?: function,
 *     onAfterAddToCart?: function,
 *     engraveText?: string
 *   }),
 * }} AddToCartActionContextType
 */

/**
 * This is the context for managing adding products to cart with associated visual feedback.
 * Used in top level App component.
 * @type {React.Context<{modal: AddToCartModalContextType, action: AddToCartActionContextType}|null>}
 */
export const AddToCartModalContext = React.createContext(null)

/**
 * Hook to access the AddToCartModalContextType.
 * @return {AddToCartModalContextType}
 */
export const useAddToCartModalContext = () => {
    const context = useContext(AddToCartModalContext)
    if (!context) {
        throw new Error('useAddToCartModalContext must be used within a AddToCartModalProvider')
    }
    return context.modal
}

/**
 * Hook to access the AddToCartActionContextType.
 * @return {AddToCartActionContextType}
 */
export const useAddToCartActionContext = () => {
    const context = useContext(AddToCartModalContext)
    if (!context) {
        throw new Error('useAddToCartActionContext must be used within a AddToCartModalProvider')
    }
    return context.action
}

/**
 * Provider for the AddToCartModalContext.
 * @param children
 */
export const AddToCartModalProvider = ({children}) => {
    const modalContext = useAddToCartModal()
    const {context: actionContext, children: actionChildren} = useAddToCartAction()
    const bonusContext = useBonusProductsModal()
    const context = useMemo(
        () => ({modal: modalContext, action: actionContext}),
        [modalContext, actionContext]
    )
    return (
        <AddToCartModalContext.Provider value={context}>
            <BonusProductsModalContext.Provider value={bonusContext}>
                {children}
                {actionChildren}
                {modalContext.isOpen && <AddToCartModal />}
                <BonusProductsModal />
            </BonusProductsModalContext.Provider>
        </AddToCartModalContext.Provider>
    )
}
AddToCartModalProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export {default as AddToCartModal} from '../components/add-to-cart-modal'

/**
 * Hook for managing the AddToCartModal state.
 * @return {AddToCartModalContextType}
 */
export const useAddToCartModal = () => {
    const [state, setState] = useState({
        isOpen: false,
        data: null,
        finalFocusRef: null
    })

    const {pathname} = useLocation()
    useEffect(() => {
        setState((state) =>
            state.isOpen
                ? {
                      ...state,
                      isOpen: false,
                      data: null,
                      finalFocusRef: null
                  }
                : state
        )
    }, [pathname])

    const onClose = useCallback(() => {
        state?.onClose?.()
        setState((oldState) => ({
            finalFocusRef: oldState.finalFocusRef,
            isOpen: false,
            data: null
        }))
    }, [state])

    return {
        isOpen: state.isOpen,
        data: state.data,
        finalFocusRef: state.finalFocusRef,
        onRecommendedProductClick: (...args) => {
            state?.onRecommendedProductClick?.(...args)
            // close the modal when a recommended product is clicked
            onClose()
        },
        onOpen: ({onRecommendedProductClick, onClose} = {}) => {
            setState((oldState) => ({
                ...oldState,
                isOpen: true,
                onRecommendedProductClick,
                onClose
            }))
        },
        onClose,
        setFinalFocusRef: (finalFocusRef) => {
            setState((oldState) => ({
                ...oldState,
                finalFocusRef
            }))
        },
        addProduct: (product, quantity = 1) => {
            setState((oldState) => {
                if (!oldState.data) {
                    const itemsAdded = getProductsToAddToCart(product, quantity)
                    // customizable product set may add less quantity than requested for extensions and accessories
                    const [count, insufficientQuantity] = getProductAddToBasketItems(
                        product,
                        null,
                        quantity
                    ).reduce(
                        ([acc, insufficientQuantity], item) => [
                            acc + item.quantity,
                            insufficientQuantity || item.quantity < quantity
                        ],
                        [0, false]
                    )

                    return {
                        ...oldState,
                        data: {
                            product,
                            itemsAdded,
                            count,
                            insufficientQuantity
                        }
                    }
                } else {
                    return {
                        ...oldState,
                        data: {
                            ...oldState.data,
                            itemsAdded: [...oldState.data.itemsAdded, product],
                            count: oldState.data.count + quantity
                        }
                    }
                }
            })
        }
    }
}

/**
 * A sequence to use to generate unique ids for products to add to cart.
 * @type {number}
 */
let sequence = 0

/**
 * Hook for handling the add to cart action context.
 * @return {{context: AddToCartActionContextType, children: React.ReactNode}}
 */
const useAddToCartAction = () => {
    // keep the product to add requests in a state to handle adding the product to cart after all dependent data is loaded
    const [requests, setRequests] = useState([])

    const addProduct = useCallback(
        ({
            product,
            quantity,
            includeInAddToCartModal = true,
            showAddToCartModal = true,
            onRecommendedProductClick,
            onAfterAddToCart,
            engraveText
        }) => {
            setRequests((existingProducts) => [
                ...existingProducts,
                {
                    product,
                    quantity,
                    includeInAddToCartModal,
                    showAddToCartModal,
                    onRecommendedProductClick,
                    onAfterAddToCart,
                    engraveText,
                    id: sequence++
                }
            ])
        },
        []
    )

    // mark requests from old pages to skip showing modals
    const {pathname} = useLocation()
    const customerId = useCustomerId()
    useEffect(() => {
        setRequests((list) =>
            list.map((item) => (item.fromOldPage ? item : {...item, fromOldPage: true}))
        )
    }, [pathname])

    useEffect(() => {
        // reset product data in case of customer change (login/logout) in order to
        // prevent from adding products that are no longer used to be added to basket
        setRequests([])
    }, [customerId])

    const context = useMemo(() => ({addProduct}), [addProduct])
    return {
        context,
        children: requests.map(({id, ...productToAdd}) => (
            <AddToCartAction
                {...productToAdd}
                // use the unique id to prevent recreation of the component when the list changes
                key={id}
                onFinished={() =>
                    // remove the request from the list after it has been processed
                    setRequests((list) => list.filter((item) => item.id !== id))
                }
            />
        ))
    }
}

/**
 * Add to cart action component - executes adding to cart logic with display of bonus product modal and add to cart modal.
 */
const AddToCartAction = ({
    fromOldPage,
    engraveText,
    product,
    quantity,
    includeInAddToCartModal,
    showAddToCartModal,
    onAfterAddToCart,
    onFinished,
    onRecommendedProductClick
}) => {
    const {handleAddToCart, isLoading} = useAddToCartImplementation(product, {
        fromOldPage,
        includeInAddToCartModal,
        quantity,
        showAddToCartModal,
        onFinished,
        onRecommendedProductClick
    })
    const onLoaded = useEffectEvent(() => {
        handleAddToCart({onAfterAddToCart, engraveText})
    })
    useEffect(() => {
        if (!isLoading) {
            onLoaded()
        }
    }, [isLoading, onLoaded])
    return null
}
AddToCartAction.propTypes = {
    fromOldPage: PropTypes.bool,
    engraveText: PropTypes.string,
    product: PropTypes.object,
    quantity: PropTypes.number,
    includeInAddToCartModal: PropTypes.bool,
    showAddToCartModal: PropTypes.bool,
    onAfterAddToCart: PropTypes.func,
    onFinished: PropTypes.func,
    onRecommendedProductClick: PropTypes.func
}
